import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { t } from 'i18next';

import { BaseComponent } from './BaseComponent';
import { LayoutType } from '../constants/LayoutConstants';
import { SidebarPosition } from '../stores/PersistentStore';
import { SidebarModule, Overlay } from '../constants/Enums';
import { TooltipIds } from '../constants/TooltipIds';
import { ViewSettingsStore } from '../stores/ViewSettingsStore';
import { VisibilityStore } from '../stores/VisibilityStore';
import { SceneStore } from '../stores/SceneStore';
import { ArticleStore } from '../stores/ArticleStore';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';
import { TooltipStore } from '../stores/TooltipStore';
import { UIHelper } from '../utils/UIHelper';
import { AppConfigurationStore } from '../stores/AppConfigurationStore';
import { ExportButton } from './ExportButton';
import { SidebarButton } from './SidebarButton';
import { KompanBase } from '../Kompan';

interface SidebarProps {
    layoutType: LayoutType;
    sidebarOrientation: SidebarPosition;
}

interface SidebarState {
    sidebar: SidebarModule;
    overlay: Overlay;
    selectionCount: number;
    articleCount: number;
    projectLoaded: boolean;
    activeTooltip: TooltipIds | null;
}

interface SidebarButtonOption {
    name: string;
    module: SidebarModule;
    disabled: boolean;
    active: boolean;
    newsCount?: number;
    infotext?: string;
    label?: string;
}

export class Sidebar extends BaseComponent<SidebarProps, SidebarState> {
    public componentWillMount(): void {
        this.addStoreListener(ViewSettingsStore, this.onChange);
        this.addStoreListener(VisibilityStore, this.onChange);
        this.addStoreListener(SceneStore, this.onChange);
        this.addStoreListener(ArticleStore, this.onChange);
        this.addStoreListener(ProjectManagerStore, this.onChange);
        this.addStoreListener(TooltipStore, this.onChange);
        this.onChange();
    }

    public componentDidUpdate(): void {
        UIHelper.updateScrollbar(ReactDOM.findDOMNode(this) as Element, '.sidebar');
    }

    public shouldComponentUpdate(nextProps: SidebarProps, nextState: SidebarState): boolean {
        return this.state !== nextState || this.props !== nextProps;
    }

    public render(): JSX.Element {
        if (
            this.state.overlay === Overlay.FLOORPLAN
        ) {
            return (
                <div className={this.getCSSClass()}/>
            );
        }

        return (
            <div className={this.getCSSClass()}>
                {this.getButtonList().map((sidebarButtonOption: SidebarButtonOption, index: number, buttonList: Array<SidebarButtonOption>) => {
                    return (
                        <SidebarButton
                            name={sidebarButtonOption.name}
                            module={sidebarButtonOption.module}
                            disabled={sidebarButtonOption.disabled}
                            active={sidebarButtonOption.active}
                            newsCount={sidebarButtonOption.newsCount}
                            infotext={sidebarButtonOption.infotext}
                            count={buttonList.length}
                            layoutType={this.props.layoutType}
                            label={sidebarButtonOption.label}
                            key={index}
                        />
                    );
                })}
            <ExportButton />
            </div>
        );
    }

    protected onChange = (): void => {
        this.setState({
            sidebar: ViewSettingsStore.getState().sidebar,
            overlay: VisibilityStore.getState().overlay,
            selectionCount: SceneStore.getState().selectedElements.length,
            articleCount: ArticleStore.getState().articleCount,
            projectLoaded: ProjectManagerStore.getCurrentProject() != null,
            activeTooltip: TooltipStore.getState().activeId
        });
    };

    protected getCSSClass = (): string => {
        return (
            'sidebar' +
            ((this.props.sidebarOrientation === 'right') ? ' sidebar-right' : '')
        );
    };

    protected getButtonList = (): Array<SidebarButtonOption> => {
        const result: Array<SidebarButtonOption> = [];

        if (!KompanBase.openedByQuoteIt) {
            result.push({
                module: SidebarModule.CATALOG, name: 'catalog', disabled: false,
                active: (this.state.sidebar === SidebarModule.CATALOG),
                infotext: this.state.activeTooltip === TooltipIds.CATALOG_BUTTON_ON_STARTUP ? t('Open the catalog to insert an article') : null,
                label: t('Catalogs')
            });
        }
        result.push({
            module: SidebarModule.PROPERTIES, name: 'property-editor', disabled: this.state.selectionCount === 0,
            active: (this.state.sidebar === SidebarModule.PROPERTIES),
            infotext: this.state.activeTooltip === TooltipIds.PROPERTY_PANEL_AFTER_ARTICLE_ADD ? t('Select an article to configure it') : null,
            label: t('Configure')
        });
        result.push({
            module: SidebarModule.ARTICLELIST, name: 'article-list', disabled: this.state.articleCount === 0,
            active: (this.state.sidebar === SidebarModule.ARTICLELIST),
            label: t('Article List')
        });
        const multipleShareOptions: boolean = AppConfigurationStore.getState().shareTypes.filter(
            (shareOption) => {return (shareOption.disabled === false); }
        ).length > 1;
        if ((this.props.layoutType === LayoutType.SMALL || false) && multipleShareOptions) {
            result.push({
                module: SidebarModule.SHARE,
                name: 'share-tab',
                disabled: this.state.articleCount === 0,
                active: (
                    this.state.sidebar != null &&
                    [
                        SidebarModule.SHARE_MAIL,
                        SidebarModule.SHARE,
                        SidebarModule.SHARE_APP,
                        SidebarModule.SHARE_DL,
                        SidebarModule.SHARE_FORMULAR
                    ].indexOf(this.state.sidebar) !== -1
                ),
                label: t('Share')
            });
        } else {
            if (AppConfigurationStore.shareFormatAvailable('App')) {
                result.push({
                    module: SidebarModule.SHARE_APP, name: 'download-tab', disabled: this.state.articleCount === 0,
                    active: (this.state.sidebar === SidebarModule.SHARE_APP),
                    label: t('Share')
                });
            }
            if (AppConfigurationStore.shareFormatAvailable('Mail')) {
                result.push({
                    module: SidebarModule.SHARE_MAIL, name: 'send-via-mail', disabled: this.state.articleCount === 0,
                    active: (this.state.sidebar === SidebarModule.SHARE_MAIL),
                    label: t('Share')
                });
            }
            if (AppConfigurationStore.shareFormatAvailable('Formular')) {
                result.push({
                    module: SidebarModule.SHARE_FORMULAR, name: 'send-via-mail', disabled: this.state.articleCount === 0,
                    active: (this.state.sidebar === SidebarModule.SHARE_FORMULAR),
                    label: t('Share')
                });
            }
            if (AppConfigurationStore.shareFormatAvailable('Download')) {
                result.push({
                    module: SidebarModule.SHARE_DL, name: 'download-tab', disabled: this.state.articleCount === 0,
                    active: (this.state.sidebar === SidebarModule.SHARE_DL),
                    label: t('Download')
                });
            }
        }
        return result;
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Sidebar.tsx